/**
 * Ryedical — Animations, Interactions & Scroll Behaviour
 * v4 — March 2026
 */
(function () {
  'use strict';

  /* ---- Respect reduced-motion preference ---- */
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ==========================================================================
     Scroll Reveal — data-animate system (v4)
     ========================================================================== */
  function initScrollReveal() {
    var elements = document.querySelectorAll('[data-animate]');
    if (!elements.length) return;

    if (prefersReducedMotion) {
      elements.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    elements.forEach(function (el) { observer.observe(el); });

    // Stagger children with data-stagger on parent
    document.querySelectorAll('[data-stagger]').forEach(function (parent) {
      var delay = parseInt(parent.dataset.stagger) || 80;
      parent.querySelectorAll('[data-animate]').forEach(function (child, i) {
        child.style.transitionDelay = (i * delay) + 'ms';
      });
    });
  }

  /* ==========================================================================
     Scroll Reveal — rye-reveal system (v3 backward compat)
     ========================================================================== */
  function initScrollRevealLegacy() {
    var reveals = document.querySelectorAll('.rye-reveal');
    if (!reveals.length) return;

    if (prefersReducedMotion) {
      reveals.forEach(function (el) { el.classList.add('rye-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var delay = parseFloat(el.getAttribute('data-rye-delay')) || 0;
          setTimeout(function () { el.classList.add('rye-revealed'); }, delay * 1000);
          observer.unobserve(el);
        }
      });
    }, { threshold: 0.15 });

    reveals.forEach(function (el) { observer.observe(el); });
  }

  /* ==========================================================================
     Smooth Scroll for Anchor Links
     ========================================================================== */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;
      var targetId = link.getAttribute('href');
      if (targetId === '#') return;
      var target = document.querySelector(targetId);
      if (!target) return;
      e.preventDefault();
      var top = target.getBoundingClientRect().top + window.pageYOffset - 72;
      window.scrollTo({ top: top, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });
  }

  /* ==========================================================================
     Sticky Add-to-Cart Bar (v4)
     Uses #ryedical-product-card and #ryedical-sticky-atc
     ========================================================================== */
  function initStickyATC() {
    var stickyBar = document.getElementById('ryedical-sticky-atc');
    if (!stickyBar) return;

    var mode = stickyBar.getAttribute('data-mode');

    function showBar() {
      stickyBar.classList.add('is-visible');
      stickyBar.setAttribute('aria-hidden', 'false');
    }
    function hideBar() {
      stickyBar.classList.remove('is-visible');
      stickyBar.setAttribute('aria-hidden', 'true');
    }

    if (mode === 'pdp') {
      /* ============== PDP MODE ==============
         Single observer on the main Add to Cart button area.
         Show when it scrolls out of view above the viewport. */
      var pdpTarget = document.querySelector('.product-form__buttons');
      if (!pdpTarget) return;

      var pdpObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            hideBar();
          } else if (entry.boundingClientRect.top < 0) {
            showBar();
          }
        });
      }, { threshold: 0 });
      pdpObserver.observe(pdpTarget);

    } else {
      /* ============== HOMEPAGE MODE ==============
         TWO observers — fixes the bug where the bar only appeared AFTER
         the visitor passed the product card.

         Observer 1 (HERO): show the bar when hero exits the viewport above.
         Observer 2 (PRODUCT CARD): hide the bar when product card enters view.

         Result: bar is visible across the entire mid-funnel (~7%–66% scroll)
         exactly where it's needed. Hides naturally when product card is on screen.
         After product card scrolls past, bar stays hidden (other CTAs handle
         the post-product-card sections — FAQ CTA, final CTA). */
      var heroSection = document.getElementById('ryedical-hero');
      var productCard = document.getElementById('ryedical-product-card');

      if (heroSection) {
        var heroObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              /* Hero is back in view — user scrolled up to top — hide bar */
              hideBar();
            } else if (entry.boundingClientRect.top < 0) {
              /* Hero has scrolled above the viewport — show bar */
              showBar();
            }
          });
        }, { threshold: 0 });
        heroObserver.observe(heroSection);
      }

      if (productCard) {
        var cardObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              /* Product card is on screen — purchase mechanism is visible — hide bar */
              hideBar();
            }
            /* When product card leaves viewport (above OR below) we don't act —
               hero observer governs the show state. */
          });
        }, { threshold: 0 });
        cardObserver.observe(productCard);
      }

      /* Homepage CTA button — smooth scroll to product card on click */
      var ctaBtn = stickyBar.querySelector('.ryedical-sticky-atc__btn');
      if (ctaBtn && productCard) {
        ctaBtn.addEventListener('click', function (e) {
          e.preventDefault();
          productCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }

    /* PDP mode — sticky button triggers the MAIN product form's submit.
       This inherits all the Taste theme's AJAX cart-drawer behaviour
       (cart drawer slide-in, cart icon update, current variant selection). */
    if (mode === 'pdp') {
      var triggerBtn = stickyBar.querySelector('.ryedical-sticky-atc__btn--trigger');
      if (triggerBtn) {
        triggerBtn.addEventListener('click', function (e) {
          e.preventDefault();
          if (triggerBtn.disabled) return;

          /* Find the main product form on the page */
          var mainForm = document.querySelector('product-form form');
          if (!mainForm) {
            console.error('Ryedical sticky ATC: main product form not found');
            return;
          }

          /* Briefly show "Adding..." feedback */
          var originalText = triggerBtn.textContent;
          triggerBtn.disabled = true;
          triggerBtn.textContent = 'Adding...';

          /* Submit the main form — product-form.js intercepts and runs the AJAX flow */
          if (typeof mainForm.requestSubmit === 'function') {
            mainForm.requestSubmit();
          } else {
            /* Fallback for older browsers */
            var submitBtn = mainForm.querySelector('[type="submit"]');
            if (submitBtn) submitBtn.click();
          }

          /* Restore button state after a short delay (the cart drawer
             slide-in is the real visual confirmation) */
          setTimeout(function () {
            triggerBtn.disabled = false;
            triggerBtn.textContent = originalText;
          }, 1200);
        });
      }
    }
  }

  /* ==========================================================================
     FAQ Accordion (v4 classes: .ryedical-faq__question / .ryedical-faq__item)
     ========================================================================== */
  function initFAQ() {
    var questions = document.querySelectorAll('.ryedical-faq__question');
    if (!questions.length) return;

    questions.forEach(function (question, index) {
      question.addEventListener('click', function () {
        var item = this.closest('.ryedical-faq__item');
        var answer = item.querySelector('.ryedical-faq__answer');
        var isOpen = item.classList.contains('is-open');

        // Close all
        document.querySelectorAll('.ryedical-faq__item.is-open').forEach(function (openItem) {
          openItem.classList.remove('is-open');
          var a = openItem.querySelector('.ryedical-faq__answer');
          if (a) a.style.maxHeight = '0';
        });

        // Open clicked if it was closed
        if (!isOpen) {
          item.classList.add('is-open');
          answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });

      // Open Q1 by default
      if (index === 0) {
        var item = question.closest('.ryedical-faq__item');
        var answer = item.querySelector('.ryedical-faq__answer');
        item.classList.add('is-open');
        if (answer) {
          // Use setTimeout to allow layout to settle
          setTimeout(function () {
            answer.style.maxHeight = answer.scrollHeight + 'px';
          }, 50);
        }
      }
    });
  }

  /* ==========================================================================
     Header Bottom Position (for mobile menu drawer)
     ========================================================================== */
  function initHeaderBottomPosition() {
    function setIt() {
      var hw = document.querySelector('.header-wrapper, sticky-header');
      if (hw) {
        var bottom = Math.round(hw.getBoundingClientRect().bottom);
        if (bottom > 0) {
          document.documentElement.style.setProperty('--header-bottom-position', bottom + 'px');
        }
      }
    }
    setIt();
    window.addEventListener('resize', setIt);
  }

  /* ==========================================================================
     Initialise All
     ========================================================================== */
  function init() {
    initScrollReveal();
    initScrollRevealLegacy();
    initSmoothScroll();
    initStickyATC();
    initFAQ();
    initHeaderBottomPosition();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
